// Website: src/pages/MerchantOnboardingPage.tsx
// Updated for Forward payment provider
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@/shared/services/apiClient';

// Declare the custom types for React Native WebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export default function MerchantOnboardingPage() {
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('application_id');

  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if we're in a WebView (React Native)
  const isInWebView = typeof window !== 'undefined' && window.ReactNativeWebView !== undefined;

  // Fetch the Forward onboarding URL from the database
  useEffect(() => {
    const fetchOnboardingUrl = async () => {
      if (!applicationId) {
        setError('No application ID provided');
        setLoading(false);
        return;
      }

      try {
        // SECURITY: Fetch the merchant record via backend API instead of direct Supabase
        const response = await apiClient.get<{
          ok: boolean;
          merchant: { onboarding_url: string; fwd_application_id: string; status: string } | null;
        }>(`/payments/merchant-onboarding/${applicationId}`);

        if (!response.ok || !response.merchant) {
          console.error('Error fetching merchant');
          throw new Error('Merchant application not found');
        }

        const merchant = response.merchant;

        if (!merchant.onboarding_url) {
          throw new Error('Onboarding URL not available');
        }

        // If already active, show success message
        if (merchant.status === 'ACTIVE') {
          handleSuccess();
          return;
        }

        setOnboardingUrl(merchant.onboarding_url);
      } catch (err: any) {
        console.error('Error:', err);
        setError(err.message || 'Failed to load onboarding form');
      } finally {
        setLoading(false);
      }
    };

    fetchOnboardingUrl();
  }, [applicationId]);

  // Listen for messages from the iframe (Forward's onboarding form)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the message is from Forward
      if (!event.origin.includes('getfwd.com') && !event.origin.includes('forward')) {
        return;
      }

      console.log('Message from Forward:', event.data);

      // Handle different message types
      if (event.data?.type === 'onboarding_complete' || event.data?.status === 'completed') {
        handleSuccess();
      } else if (event.data?.type === 'onboarding_cancelled') {
        handleCancel();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isInWebView, applicationId]);

  const handleSuccess = () => {
    console.log('✅ Onboarding completed!');

    if (isInWebView && window.ReactNativeWebView) {
      console.log('📤 Posting success message to React Native');
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'onboarding_success',
          application_id: applicationId,
        })
      );
    } else {
      // For browser, show success message (independent flow)
      setError(null);
      setOnboardingUrl(null);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    console.log('❌ Onboarding cancelled');

    if (isInWebView && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'onboarding_cancelled',
        })
      );
    } else {
      // For browser, just go back (independent flow)
      window.history.back();
    }
  };

  const handleBackToApp = () => {
    if (isInWebView && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'onboarding_success',
          application_id: applicationId,
        })
      );
    } else {
      window.close();
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading merchant onboarding...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>Onboarding Error</h2>
          <p style={styles.errorText}>{error}</p>
          <button onClick={handleBackToApp} style={styles.button}>
            {isInWebView ? 'Back to App' : 'Close'}
          </button>
        </div>
      </div>
    );
  }

  // Success state (merchant is already active)
  if (!onboardingUrl) {
    return (
      <div style={styles.container}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>✓</div>
          <h1 style={styles.successTitle}>Merchant Setup Complete!</h1>
          <p style={styles.successText}>
            Your merchant account is active. You can now start creating invoices and accepting payments.
          </p>
          <button onClick={handleBackToApp} style={styles.successButton}>
            {isInWebView ? 'Back to App' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      {/* Header - only show in browser, not in WebView */}
      {!isInWebView && (
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h1 style={styles.headerTitle}>Complete Your Merchant Setup</h1>
            <p style={styles.headerSubtitle}>
              Please fill out the form below to activate your merchant account and start accepting payments.
            </p>
          </div>
        </div>
      )}

      {/* iFrame Container */}
      <div style={styles.iframeContainer}>
        <iframe
          src={onboardingUrl}
          style={styles.iframe}
          title="Forward Merchant Onboarding"
          allow="payment"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        />
      </div>

      {/* Footer - only show in browser */}
      {!isInWebView && (
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Need help? Contact support at{' '}
            <a href="mailto:support@wlkthru.co" style={styles.footerLink}>
              support@wlkthru.co
            </a>
          </p>
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  // Loading state
  container: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#6b7280',
    fontSize: '16px',
  },

  // Error state
  errorCard: {
    maxWidth: '500px',
    padding: '48px 32px',
    backgroundColor: 'white',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '12px',
  },
  errorText: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '24px',
  },

  // Success state
  successCard: {
    maxWidth: '500px',
    padding: '48px 32px',
    backgroundColor: 'white',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 24px',
    backgroundColor: '#10b981',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 'bold',
    lineHeight: '64px',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '12px',
  },
  successText: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  successButton: {
    padding: '12px 32px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },

  // Main page with iframe
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e5e7eb',
    padding: '32px 20px',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '8px',
  },
  headerSubtitle: {
    fontSize: '16px',
    color: '#6b7280',
  },
  iframeContainer: {
    flex: 1,
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: '20px',
  },
  iframe: {
    width: '100%',
    height: '800px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  footer: {
    padding: '24px 20px',
    textAlign: 'center',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: 'white',
  },
  footerText: {
    color: '#6b7280',
    fontSize: '14px',
  },
  footerLink: {
    color: '#3b82f6',
    textDecoration: 'none',
  },

  // Buttons
  button: {
    padding: '10px 24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
};

// Add CSS animation for spinner
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}
